/**
 * Which cookie banner design a visitor sees (SDUI-1604).
 *
 * We serve two PECR-compliant banner designs to GB traffic and compare how much consent each
 * one holds up. Cookiebot has no native multivariant support, so the variation is carried by
 * the domain group id passed to `uc.js`, and the choice of id is ours to make.
 *
 * Deliberately not derived from `CS_ANONYMOUS_ID`, which the shaping doc originally proposed.
 * On the Gatsby site the banner is injected during render while that cookie is only written
 * later in a `useEffect`, so on a genuine first visit there is nothing to derive from. A coin
 * flip written down at the point of decision has no such ordering problem and needs no other
 * identifier to exist first.
 *
 * Everything here is pure. The two adapters that touch a cookie jar live next to their stack:
 * `../banner-variant` for the browser, and `sdui-app-cookies` for the SSR request.
 */

/**
 * Records which banner a visitor was assigned.
 *
 * Strictly necessary: its only purpose is choosing which consent banner to show, it is read
 * before any consent exists, and it carries no identifier. Scoped to the parent domain so the
 * assignment survives the www-to-app hop, matching the attributes SDUI-1442 settled on for the
 * consent mirror.
 */
export const BANNER_VARIANT_COOKIE = 'CS_COOKIE_BANNER_VARIANT';

/** `a` is the existing layout with the non-essential categories unticked; `b` is the three-button design. */
export type BannerVariant = 'a' | 'b';

const VARIANTS: readonly BannerVariant[] = ['a', 'b'];

/**
 * Hosts the experiment may run on.
 *
 * Explicit rather than pattern-matched, for the same reason `SHARE_DOMAINS` is: a host that is
 * not listed resolves to the single existing domain group, which is the safe answer. Cookiebot
 * only renders a banner on a host registered to the domain group, so the two non-production
 * entries are the registered aliases we actually use: `localhost` for the SSR dev server and
 * `web-dev-stg.cs55.co.uk` for staging.
 */
const EXPERIMENT_HOSTS = ['clearscore.com', 'www.clearscore.com', 'app.clearscore.com', 'localhost', 'web-dev-stg.cs55.co.uk'];

/**
 * Market path prefixes that are not GB.
 *
 * Mirrors `MARKETS` in `lib.constants.market` minus `gb`, kept as a literal here so this
 * package stays dependency-free like the rest of it. `clearscore-website` serves every market
 * from one deployment with no path allow-list, so `MARKET` is a per-deployment constant there
 * and cannot be used as the gate. The path is the only thing that distinguishes them.
 *
 * Note `/uk/...` is deliberately absent: those are GB pages under a legacy prefix.
 */
const NON_GB_MARKET_PREFIXES = ['za', 'au', 'ca', 'nz'];

/**
 * The three domain groups in play.
 *
 * Each arm is its own group, and neither is the existing one. That group pre-ticks the
 * non-essential categories, which is the breach this test exists to fix, so it can never be an
 * arm: it would win on accept rate precisely because it is non-compliant, and the winner could
 * not be shipped. It is shared by every market, too, so unticking it in place would change the
 * banner everywhere and mix every market's traffic into one arm's numbers.
 */
export interface BannerExperimentConfig {
    /** The existing domain group. Served everywhere outside the experiment, and never as an arm. */
    controlCookiebotId: string;
    /** Variant A: today's layout with the non-essential categories unticked. */
    variantACookiebotId?: string;
    /** Variant B: Accept, Deny and Customise, with no toggles. */
    variantBCookiebotId?: string;
}

/** What was served: one of the arms, or the existing group outside the experiment. */
export type ServedBannerVariant = BannerVariant | 'control';

export interface BannerVariantResolution {
    variant: ServedBannerVariant;
    /** The domain group id to hand to `uc.js`. */
    cookiebotId: string;
    /** Set when this call assigned a variant that now needs writing down, null otherwise. */
    minted: BannerVariant | null;
}

/**
 * How the experiment is configured.
 *
 * - `split`: both arms are configured, and GB visitors are divided between them.
 * - `a-only`: only Variant A is. Every GB visitor gets it. This is where the test goes when it
 *   is stopped, which keeps GB compliant instead of dropping it back onto the pre-ticked group.
 * - `off`: Variant A is not configured, so nothing changes. The state before launch.
 *
 * The split never switches on with one arm missing, because the only thing left to fill the gap
 * would be the existing group.
 */
export type BannerExperimentMode = 'off' | 'a-only' | 'split';

export const getBannerExperimentMode = ({
    controlCookiebotId,
    variantACookiebotId,
    variantBCookiebotId,
}: BannerExperimentConfig): BannerExperimentMode => {
    // With no control id Cookiebot is not configured on this deployment at all, and the rest of
    // the stack treats that as "no banner". Injecting an id here would change that behaviour.
    if (!controlCookiebotId || !variantACookiebotId) return 'off';

    // B must be a design of its own. As the existing group it would make one arm non-compliant,
    // and as A's group there would be nothing to compare.
    const hasDistinctB =
        Boolean(variantBCookiebotId) &&
        variantBCookiebotId !== controlCookiebotId &&
        variantBCookiebotId !== variantACookiebotId;

    return hasDistinctB ? 'split' : 'a-only';
};

/** Whether this host and path are in the experiment. GB only, and only the two logged-out web hosts. */
export const isBannerExperimentSurface = (hostname: string, pathname: string): boolean => {
    if (!EXPERIMENT_HOSTS.includes(hostname.toLowerCase())) return false;

    const path = pathname.toLowerCase();
    return !NON_GB_MARKET_PREFIXES.some((market) => path === `/${market}` || path.startsWith(`/${market}/`));
};

/** A recorded assignment, or null when the value is absent or not one we wrote. */
export const parseBannerVariant = (value: string | null | undefined): BannerVariant | null =>
    VARIANTS.find((variant) => variant === value) ?? null;

/**
 * An even coin flip.
 *
 * A byte from the platform CSPRNG rather than `Math.random`, so the split cannot drift with a
 * weak or seeded implementation. The low bit of a uniform byte is exactly 50/50. Falls back to
 * `Math.random` only where no crypto is exposed, which is good enough for a banner split.
 */
const flipCoin = (): BannerVariant => {
    const cryptoApi = globalThis.crypto;

    if (typeof cryptoApi?.getRandomValues === 'function') {
        const buffer = new Uint8Array(1);
        cryptoApi.getRandomValues(buffer);
        return (buffer[0] & 1) === 0 ? 'a' : 'b';
    }

    return Math.random() < 0.5 ? 'a' : 'b';
};

/**
 * Decides which banner to serve.
 *
 * Outside the experiment, or before Variant A is configured, this returns the existing group
 * and mints nothing, so every other market and brand is untouched and no cookie is written.
 */
export const resolveBannerVariant = ({
    config,
    hostname,
    pathname,
    recorded,
}: {
    config: BannerExperimentConfig;
    hostname: string;
    pathname: string;
    /** The value already in the cookie jar, if any. */
    recorded: string | null | undefined;
}): BannerVariantResolution => {
    const control: BannerVariantResolution = {
        variant: 'control',
        cookiebotId: config.controlCookiebotId,
        minted: null,
    };

    if (!isBannerExperimentSurface(hostname, pathname)) return control;

    const mode = getBannerExperimentMode(config);
    if (mode === 'off') return control;

    const variantACookiebotId = config.variantACookiebotId as string;

    // Stopped: everyone back on one banner. A recorded assignment is ignored rather than
    // honoured, so B's visitors return to A too, and nothing is minted, so visitors who first
    // arrive while the test is paused are assigned normally if it restarts.
    if (mode === 'a-only') return { variant: 'a', cookiebotId: variantACookiebotId, minted: null };

    const existing = parseBannerVariant(recorded);
    const variant = existing ?? flipCoin();

    return {
        variant,
        cookiebotId: variant === 'b' ? (config.variantBCookiebotId as string) : variantACookiebotId,
        minted: existing ? null : variant,
    };
};
